import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  FileText,
  ShoppingBag,
  Package,
  Bell,
  MessageSquare,
  Eye,
  TrendingUp,
  CalendarDays,
  ArrowRight,
  ShieldCheck,
  BarChart3,
  Globe2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Swal from "sweetalert2";
import api from "../Api/Api";

const Dashboard = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);

  const [totalVisits, setTotalVisits] = useState(0);
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(true);

  // Protect admin route
  useEffect(() => {
    if (role !== "admin") {
      navigate("/");
    }
  }, [role, navigate]);

  // Fetch dashboard stats
  useEffect(() => {
    if (role !== "admin") return;

    const fetchStats = async () => {
      setLoadingStats(true);

      try {
        const response = await api.get("/get_dashboard_data/");
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Dashboard",
          text:
            error.response?.data?.message ||
            "Failed to load dashboard data.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [role]);

  // Fetch messages
  useEffect(() => {
    if (role !== "admin") return;

    const fetchMessages = async () => {
      setLoadingMessages(true);

      try {
        const response = await api.get("/get_messages/");

        setMessages(
          Array.isArray(response.data)
            ? response.data
            : response.data?.messages || []
        );
      } catch (error) {
        console.error("Error fetching messages:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Messages",
          text:
            error.response?.data?.message ||
            "Failed to load user messages.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [role]);

  // Fetch visits
  useEffect(() => {
    if (role !== "admin") return;

    const fetchVisits = async () => {
      setLoadingVisits(true);

      try {
        const [totalResponse, visitsResponse] = await Promise.all([
          api.get("/total_visits/"),
          api.get("/get_visits/"),
        ]);

        setTotalVisits(
          Number(totalResponse.data?.total_visits || 0)
        );

        setVisits(
          Array.isArray(visitsResponse.data)
            ? visitsResponse.data
            : visitsResponse.data?.visits || []
        );
      } catch (error) {
        console.error("Error fetching visits:", error);
      } finally {
        setLoadingVisits(false);
      }
    };

    fetchVisits();
  }, [role]);

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("en-KE");
  };

  const formatCurrency = (value) => {
    return `KES ${Number(value || 0).toLocaleString("en-KE", {
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const salesChartData = useMemo(() => {
    if (!stats) return [];

    return [
      {
        name: "Daily",
        amount: Number(stats.daily_sales || 0),
      },
      {
        name: "Weekly",
        amount: Number(stats.weekly_sales || 0),
      },
      {
        name: "Monthly",
        amount: Number(stats.monthly_sales || 0),
      },
      {
        name: "Yearly",
        amount: Number(stats.yearly_sales || 0),
      },
    ];
  }, [stats]);

  const topStats = useMemo(() => {
    if (!stats) return [];

    return [
      {
        title: "Users",
        value: stats.total_users,
        icon: Users,
      },
      {
        title: "Blog Posts",
        value: stats.total_blog_posts,
        icon: FileText,
      },
      {
        title: "Orders",
        value: stats.total_orders,
        icon: ShoppingBag,
      },
      {
        title: "Products",
        value: stats.total_products,
        icon: Package,
      },
      {
        title: "Notifications",
        value: stats.total_notifications,
        icon: Bell,
      },
      {
        title: "Messages",
        value: stats.total_messages,
        icon: MessageSquare,
      },
      {
        title: "Website Visits",
        value: totalVisits,
        icon: Eye,
      },
    ];
  }, [stats, totalVisits]);

  if (role !== "admin") {
    return null;
  }

  if (loadingStats) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="h-48 rounded-3xl bg-slate-200 animate-pulse mb-8" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4, 5, 6, 7].map((item) => (
              <div
                key={item}
                className="h-32 rounded-3xl bg-slate-200 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">
          Dashboard data is unavailable.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
                <ShieldCheck size={15} />
                Admin Dashboard
              </div>

              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Business Overview
              </h1>

              <p className="text-slate-300 text-lg leading-relaxed">
                Monitor users, sales, products, website activity,
                messages, and platform performance from one place.
              </p>
            </div>

            <button
              onClick={() => navigate("/products")}
              className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-blue-500 hover:-translate-y-0.5 transition-all"
            >
              Manage Products
              <ArrowRight
                size={17}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </section>

      <main className="relative -mt-14 px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* KPI Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {topStats.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5 hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-500 mb-2">
                        {item.title}
                      </p>

                      <p className="text-3xl font-bold">
                        {formatNumber(item.value)}
                      </p>
                    </div>

                    <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                      <Icon size={21} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sales */}
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6 mb-8">
            {/* Sales Summary */}
            <section className="bg-white border border-slate-200 rounded-3xl p-7 shadow-xl shadow-slate-900/5">
              <div className="flex items-center justify-between mb-7">
                <div>
                  <p className="text-blue-600 text-sm font-semibold mb-1">
                    Revenue
                  </p>

                  <h2 className="text-2xl font-bold">
                    Sales Summary
                  </h2>
                </div>

                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <TrendingUp size={21} />
                </div>
              </div>

              <div className="space-y-4">
                {[
                  ["Daily", stats.daily_sales],
                  ["Weekly", stats.weekly_sales],
                  ["Monthly", stats.monthly_sales],
                  ["Yearly", stats.yearly_sales],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between rounded-2xl bg-slate-50 px-5 py-4"
                  >
                    <span className="text-sm text-slate-500">
                      {label}
                    </span>

                    <span className="font-bold text-slate-900">
                      {formatCurrency(value)}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Chart */}
            <section className="bg-white border border-slate-200 rounded-3xl p-7 shadow-xl shadow-slate-900/5">
              <div className="flex items-center justify-between mb-7">
                <div>
                  <p className="text-blue-600 text-sm font-semibold mb-1">
                    Performance
                  </p>

                  <h2 className="text-2xl font-bold">
                    Sales Trends
                  </h2>
                </div>

                <BarChart3
                  size={22}
                  className="text-blue-600"
                />
              </div>

              <div className="h-[330px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                    />

                    <Tooltip
                      formatter={(value) => [
                        formatCurrency(value),
                        "Sales",
                      ]}
                    />

                    <Bar
                      dataKey="amount"
                      fill="#2563EB"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          {/* Visitors */}
          <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden mb-8">
            <div className="p-6 md:p-7 border-b border-slate-200 flex items-center justify-between gap-5">
              <div>
                <p className="text-blue-600 text-sm font-semibold mb-1">
                  Analytics
                </p>

                <h2 className="text-2xl font-bold">
                  Website Visitors
                </h2>
              </div>

              <div className="hidden sm:flex items-center gap-2 text-slate-500">
                <Globe2 size={17} className="text-blue-600" />
                {formatNumber(totalVisits)} visits
              </div>
            </div>

            {loadingVisits ? (
              <div className="p-7 space-y-3">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="h-12 bg-slate-100 rounded-xl animate-pulse"
                  />
                ))}
              </div>
            ) : visits.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                No website visits recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-7 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        IP Address
                      </th>

                      <th className="px-7 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Page
                      </th>

                      <th className="px-7 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Time Visited
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {visits.map((visit, index) => (
                      <tr
                        key={visit.id || index}
                        className="hover:bg-slate-50 transition"
                      >
                        <td className="px-7 py-4 text-sm text-slate-600">
                          {visit.ip_address || "N/A"}
                        </td>

                        <td className="px-7 py-4">
                          <span className="inline-flex px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-sm font-medium">
                            {visit.page || "Unknown"}
                          </span>
                        </td>

                        <td className="px-7 py-4 text-sm text-slate-500">
                          <div className="flex items-center gap-2">
                            <CalendarDays size={14} />
                            {formatDate(visit.created_at)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Bottom Grid */}
          <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
            {/* Quick Actions */}
            <section className="bg-white border border-slate-200 rounded-3xl p-7 shadow-xl shadow-slate-900/5 h-fit">
              <p className="text-blue-600 text-sm font-semibold mb-1">
                Administration
              </p>

              <h2 className="text-2xl font-bold mb-6">
                Quick Actions
              </h2>

              <div className="space-y-3">
                {[
                  {
                    label: "Manage Products",
                    path: "/products",
                    icon: Package,
                  },
                  {
                    label: "View Orders",
                    path: "/delivery-orders",
                    icon: ShoppingBag,
                  },
                  {
                    label: "Post Article",
                    path: "/post-blog",
                    icon: FileText,
                  },
                  {
                    label: "Notifications",
                    path: "/notifications",
                    icon: Bell,
                  },
                ].map((action) => {
                  const Icon = action.icon;

                  return (
                    <button
                      key={action.path}
                      onClick={() => navigate(action.path)}
                      className="group w-full flex items-center justify-between p-4 rounded-2xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition">
                          <Icon size={18} />
                        </div>

                        <span className="font-semibold text-slate-800">
                          {action.label}
                        </span>
                      </div>

                      <ArrowRight
                        size={16}
                        className="text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition"
                      />
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Messages */}
            <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
              <div className="p-6 md:p-7 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-semibold mb-1">
                    Customer Communication
                  </p>

                  <h2 className="text-2xl font-bold">
                    User Messages
                  </h2>
                </div>

                <MessageSquare
                  size={22}
                  className="text-blue-600"
                />
              </div>

              {loadingMessages ? (
                <div className="p-7 space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-28 bg-slate-100 rounded-2xl animate-pulse"
                    />
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="py-16 text-center text-slate-500">
                  No messages received yet.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {messages.slice(0, 6).map((item, index) => (
                    <article
                      key={item.id || index}
                      className="p-6 hover:bg-slate-50/70 transition"
                    >
                      <div className="flex gap-4">
                        <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 font-bold">
                          {(item.name || "U")
                            .charAt(0)
                            .toUpperCase()}
                        </div>

                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <h3 className="font-bold text-slate-900">
                              {item.name || "Unknown User"}
                            </h3>

                            <span className="text-xs text-slate-400">
                              {formatDate(item.created_at)}
                            </span>
                          </div>

                          <p className="text-slate-600 leading-relaxed mt-2">
                            {item.message}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;